import React from 'react';
import { Instagram, Facebook, Linkedin, ExternalLink, Home, Phone } from 'lucide-react';
import logo from '../../assets/Logo.jpg';
import patron from '../../assets/Contactame_Patron.svg';

/**
 * Componente que renderiza un perfil exportable con relación de aspecto 9:16
 * Este componente se usa para generar imágenes para compartir en redes sociales
 *
 * @param {Object} props
 * @param {Object} props.providerData - Datos del proveedor
 * @param {Object} props.images - Imágenes del proveedor (foto de perfil, etc.)
 * @param {string} props.type - Tipo de proveedor ('service' o 'product')
 * @param {string} props.qrCode - URL de la imagen del código QR
 * @returns {JSX.Element}
 */
const ExportableProfile = ({ providerData, images, type, qrCode }) => {
    // Si no hay datos del proveedor o código QR, no renderizar nada
    if (!providerData || !qrCode) return null;

    const profileImage = images?.profilePhoto || images?.circularProfilePhoto;

    // Función simple para extraer el nombre de usuario de Instagram
    const extractInstagramUsername = (url) => {
        if (!url) return '';
        // Extraer el username después de instagram.com/
        const match = url.match(/instagram\.com\/([^\/\?]+)/);
        return match && match[1] ? match[1] : url;
    };

    return (
        <div id="exportable-profile" style={{
            position: 'absolute',
            top: '-9999px',
            left: '-9999px',
            width: '450px', /* Relación 9:16 */
            height: '800px', /* Relación 9:16 */
            fontFamily: 'Montserrat',
            backgroundImage: `url(${patron})`,
            backgroundSize: 'auto',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 0 15px rgba(0,0,0,0.3)',
            color: '#333',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header - Alto fijo */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
                height: '55px'
            }}>
                <img src={logo} alt='Logo' style={{ width: '62px' }} />
                <h1 style={{ fontSize: '40px', fontWeight: '800', color: '#222', marginTop: '8px', marginLeft: '1px' }}>NTACTAME</h1>
            </div>

            {/* Profile info - Alto fijo */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '15px',
                height: '120px',
                overflow: 'hidden'
            }}>
                {profileImage && <img src={profileImage} alt='Perfil' style={{ width: '90px', height: '90px', borderRadius: '50%', marginRight: '15px' }} />}
                <div style={{ color: "darkgray", overflow: 'hidden' }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: '22px',
                        fontWeight: 'bold',
                        color: '#222',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {providerData.name}
                    </h2>
                    <p style={{
                        margin: '3px 0',
                        fontSize: '14px',
                        color: '#373737',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {type === 'service' ? 'Proveedor de Servicios' : 'Proveedor de Productos'}
                    </p>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '5px',
                        maxHeight: '50px',
                        overflow: 'hidden'
                    }}>
                        {providerData.categories?.map((cat, idx) => (
                            <span
                                key={idx}
                                style={{
                                    backgroundColor: '#FFD966',
                                    padding: '2px 6px',
                                    borderRadius: '15px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                    display: 'inline-block'
                                }}
                            >
                                {cat}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Contacto y Ubicaciones en dos columnas - Alto fijo */}
            {(providerData.phone || providerData.links?.length || providerData.locations?.length > 0) && (
                <div style={{
                    display: 'flex',
                    gap: '15px',
                    marginBottom: '15px',
                    height: '120px'
                }}>
                    {/* Columna de Contacto */}
                    {(providerData.phone || providerData.links?.length) && (
                        <div style={{
                            padding: '8px 12px',
                            background: 'rgba(255,255,255,0.4)',
                            borderRadius: '8px',
                            flex: '1',
                            overflow: 'hidden'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '15px',
                                fontWeight: 'bold',
                                marginBottom: '5px'
                            }}>
                                Contacto
                            </h3>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '3px',
                                fontSize: '12px',
                                overflow: 'hidden',
                                maxHeight: '85px'
                            }}>
                                {providerData.phone && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        <Phone size={12}/> {providerData.phone}
                                    </div>
                                )}
                                {providerData.links?.map((link, i) => {
                                    const name = link.name.toLowerCase();
                                    let icon = <ExternalLink size={12}/>;

                                    if (name.includes('instagram'))
                                        icon = <Instagram size={12} style={{ color: "#000" }} />;
                                    else if (name.includes('facebook'))
                                        icon = <Facebook size={12}/>;
                                    else if (name.includes('linkedin'))
                                        icon = <Linkedin size={12} style={{ color: "#0077b5" }} />;
                                    else if (name.includes('web'))
                                        icon = <ExternalLink size={12} style={{ color: "#2196f3" }} />;

                                    // Si es Instagram y no es web, extraer solo el username
                                    const displayText = name.includes('web')
                                        ? link.url
                                        : (name.includes('instagram') ? extractInstagramUsername(link.url) : link.name);

                                    return (
                                        <div
                                            key={i}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '3px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {icon} {displayText}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Columna de Ubicaciones */}
                    {providerData.locations?.length > 0 && (
                        <div style={{
                            padding: '8px 12px',
                            background: 'rgba(255,255,255,0.4)',
                            borderRadius: '8px',
                            flex: '1',
                            overflow: 'hidden'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '15px',
                                fontWeight: 'bold',
                                marginBottom: '5px'
                            }}>
                                Ubicaciones
                            </h3>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                maxHeight: '85px',
                                overflow: 'hidden',
                                gap: '3px 10px'
                            }}>
                                {providerData.locations.map((loc, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            color: '#222222',
                                            fontSize: '12px',
                                            width: 'calc(50% - 5px)', // Dos por línea con gap
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        <Home size={12}/>
                                        <span style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {loc}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Descripción - Alto reducido */}
            <div style={{
                marginBottom: '15px',
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.4)',
                borderRadius: '8px',
                height: '160px', /* Reducido de 220px a 180px */
                overflow: 'hidden'
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: 'bold',
                    color: '#222222',
                    marginBottom: '5px'
                }}>
                    Descripción
                </h3>
                <p style={{
                    margin: '5px 0',
                    fontSize: '13px',
                    lineHeight: '1.3',
                    color: '#222',
                    maxHeight: '150px', /* Reducido para ajustarse al nuevo contenedor */
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {providerData.description || "No hay descripción disponible."}
                </p>
            </div>

            {/* QR Code - Alto ligeramente aumentado */}
            <div style={{
                textAlign: 'center',
                margin: '10px 0',
                height: '185px', /* Aumentado de 160px a 175px */
                overflow: 'hidden'
            }}>
                <p style={{
                    fontSize: '13px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#222222',
                    maxHeight: '40px',
                    overflow: 'hidden'
                }}>
                    Para conocer más sobre {providerData.name}, ingresa a contactame.com.ar/{providerData.username} o escaneá el siguiente QR:
                </p>
                <img src={qrCode} alt='QR' style={{ width: '120px', height: '120px' }} />
            </div>

            {/* CTA - Letra más pequeña */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px',
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.4)',
                borderRadius: '8px',
                height: '25px' /* Reducido de 30px a 25px */
            }}>
                <p style={{
                    margin: 0,
                    fontSize: '10px', /* Reducido de 11px a 10px */
                    color: '#222',
                    textAlign: 'center',
                    flex: 1
                }}>
                    ¿Brindás un servicio? <strong>OFRECELO</strong>
                </p>
                <p style={{
                    margin: 0,
                    fontSize: '10px', /* Reducido de 11px a 10px */
                    color: '#222',
                    textAlign: 'center',
                    flex: 1
                }}>
                    ¿Buscás un contacto? <strong>ENCONTRALO</strong>
                </p>
            </div>

            {/* Footer - Alto fijo */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                height: '50px'
            }}>
                <img src={logo} alt='Logo' style={{ width: '40px' }} />
                <div style={{ textAlign: 'center' }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: '20px',
                        fontWeight: '800',
                        color: '#222'
                    }}>
                        CONTACTAME
                    </h2>
                    <p style={{
                        margin: 0,
                        fontSize: '11px',
                        color: '#222'
                    }}>
                        contactame.com.ar | 2216973030
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ExportableProfile;
